import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CytoscapeComponent from 'react-cytoscapejs';
import type cytoscape from 'cytoscape';
import {
    Share2, RefreshCw, Camera, ExternalLink, Users, Link2, ShieldCheck,
    AlertTriangle, Fingerprint, Send,
} from 'lucide-react';
import { caseService, instagramOsintService, type InstagramProfile, type InstagramNetwork as NetworkData } from '../services/api';
import { toErrorMessage } from '../utils/safeStr';
import { useTheme } from '../hooks/useTheme';

interface CaseLite { id: string; title: string }

// ── Dedicated Instagram-Network stylesheet ──────────────────────────────
// Self-contained (imports nothing from the money-canvas stack). Presentation is
// keyed off the semantic kind / placement_role / edge_type the backend declares.
// Label colours are theme-aware: the canvas sits on zinc-50 / zinc-950, so the
// old light-on-light palette (#e5e7eb / #fbcfe8) was unreadable in light mode.
export function buildNetworkStyle(isDark: boolean): any[] {
    const label = isDark ? '#e5e7eb' : '#18181b';
    const profileLabel = isDark ? '#fce7f3' : '#831843';
    const usesLine = isDark ? '#64748b' : '#475569';
    const sharesLabel = isDark ? '#f5f3ff' : '#4c1d95';
    const sharesLabelBg = isDark ? '#1e1b4b' : '#f3e8ff';
    return [
        {
            selector: 'node',
            style: {
                label: 'data(label)', color: label, 'font-size': 11,
                'font-weight': 500,
                'text-valign': 'bottom', 'text-halign': 'center', 'text-margin-y': 6,
                'text-wrap': 'ellipsis', 'text-max-width': 140,
                'text-background-color': isDark ? '#09090b' : '#ffffff',
                'text-background-opacity': 0.75,
                'text-background-padding': 2,
                'text-background-shape': 'roundrectangle',
                'border-width': 1, 'border-color': isDark ? '#334155' : '#94a3b8',
            },
        },
        // Profiles — the people
        {
            selector: 'node[kind="ig_profile"]',
            style: {
                shape: 'round-rectangle', 'background-color': '#db2777', width: 34, height: 34,
                color: profileLabel, 'font-weight': 'bold', 'font-size': 12,
            },
        },
        { selector: 'node[kind="ig_profile"][?resolved]', style: { 'border-width': 3, 'border-color': '#22c55e' } },
        // Shared forensic artefacts — the connectors
        { selector: 'node[kind="address"]', style: { shape: 'diamond', 'background-color': '#f59e0b', width: 26, height: 26 } },
        { selector: 'node[kind="upi"]', style: { shape: 'hexagon', 'background-color': '#38bdf8', width: 24, height: 24 } },
        { selector: 'node[kind="phone"]', style: { shape: 'ellipse', 'background-color': '#34d399', width: 22, height: 22 } },
        { selector: 'node[kind="handle"]', style: { shape: 'ellipse', 'background-color': '#94a3b8', width: 22, height: 22 } },
        { selector: 'node[placement_role="shared_relay"]', style: { 'border-width': 2, 'border-color': '#fbbf24' } },
        { selector: 'node:selected', style: { 'border-width': 4, 'border-color': '#22d3ee' } },
        // Edges
        {
            selector: 'edge[edge_type="uses"]',
            style: {
                width: 2, 'line-color': usesLine, 'curve-style': 'bezier',
                'target-arrow-shape': 'none', opacity: 0.9,
            },
        },
        {
            selector: 'edge[edge_type="shares"]',
            style: {
                width: 4, 'line-color': '#a855f7', 'curve-style': 'bezier', label: 'data(label)',
                color: sharesLabel, 'font-size': 10, 'font-weight': 600,
                'text-rotation': 'autorotate',
                'text-background-color': sharesLabelBg,
                'text-background-opacity': 0.95, 'text-background-padding': 3,
                'text-background-shape': 'roundrectangle',
            },
        },
    ];
}

const LAYOUT = { name: 'cose', animate: false, padding: 40, nodeRepulsion: 9000, idealEdgeLength: 90, nodeDimensionsIncludeLabels: true } as any;

export default function InstagramNetwork() {
    const theme = useTheme();
    const isDark = theme === 'dark';
    const networkStyle = useMemo(() => buildNetworkStyle(isDark), [isDark]);
    const [cases, setCases] = useState<CaseLite[]>([]);
    const [caseId, setCaseId] = useState('');
    const [profiles, setProfiles] = useState<InstagramProfile[]>([]);
    const [network, setNetwork] = useState<NetworkData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selected, setSelected] = useState<Record<string, any> | null>(null);
    const [promoting, setPromoting] = useState(false);
    const [promoteMsg, setPromoteMsg] = useState('');
    const [resolving, setResolving] = useState(false);
    const cyRef = useRef<cytoscape.Core | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        caseService.getAll().then((d: any) => {
            const list = Array.isArray(d) ? d : (d?.cases || d?.data || []);
            setCases(list);
            if (list.length && !caseId) setCaseId(list[0].id);
        }).catch(() => setCases([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = async (cid: string) => {
        if (!cid) return;
        setLoading(true); setError(''); setSelected(null);
        try {
            const [p, n] = await Promise.all([
                instagramOsintService.listProfiles(cid),
                instagramOsintService.network(cid),
            ]);
            setProfiles(p.profiles || []);
            setNetwork(n);
        } catch (e: any) {
            setError(toErrorMessage(e) || 'Failed to load network');
            setProfiles([]); setNetwork(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { if (caseId) load(caseId); /* eslint-disable-next-line */ }, [caseId]);

    // Reference-stable element list so react-cytoscapejs only re-layouts on real changes.
    const elements = useMemo(
        () => (network ? [...network.nodes, ...network.edges] : []),
        [network],
    );

    const counts = network?.counts;

    const focusHandle = (handle: string) => {
        const cy = cyRef.current;
        if (!cy) return;
        const node = cy.getElementById(`ig_profile:${handle.toLowerCase().replace(/^@/, '')}`);
        if (node && node.length) {
            cy.elements().unselect();
            node.select();
            setSelected(node.data());
            cy.animate({ center: { eles: node }, zoom: 1.4 }, { duration: 300 });
        }
    };

    const sendToGraph = async () => {
        if (!caseId) return;
        setPromoting(true); setPromoteMsg('');
        try {
            const r = await instagramOsintService.promoteToGraph(caseId);
            setPromoteMsg(
                `Added ${r.entities} entities + ${r.edges} links to this case’s graph.`
                    + (r.same_person_links ? ` · ${r.same_person_links} same-person links` : '')
                    + ' Open Fusion Canvas (not the sidebar Fusion sandbox) to see Instagram phones/wallets/UPI merge with other case evidence.',
            );
        } catch (e: any) {
            setPromoteMsg(toErrorMessage(e) || 'Failed to add to case graph');
        } finally {
            setPromoting(false);
        }
    };

    const resolveIdentity = async (handle: string) => {
        if (!caseId || !handle) return;
        setResolving(true); setError('');
        try {
            await instagramOsintService.resolveIdentity(caseId, handle);
            await load(caseId);
            setPromoteMsg(`Identity pivots updated for @${handle.replace(/^@/, '')}.`);
        } catch (e: any) {
            setError(toErrorMessage(e) || 'Identity resolve failed');
        } finally {
            setResolving(false);
        }
    };

    const registerCy = (cy: cytoscape.Core) => {
        cyRef.current = cy;
        cy.removeAllListeners();
        cy.on('tap', 'node', (ev) => setSelected(ev.target.data()));
        cy.on('tap', (ev) => { if (ev.target === cy) setSelected(null); });
    };

    // ── selected-node detail helpers ──
    const selectedProfile = selected?.kind === 'ig_profile'
        ? profiles.find((p) => p.handle.toLowerCase() === String(selected.handle).toLowerCase())
        : null;
    const sharedByHandles = (selected && selected.kind !== 'ig_profile' && network)
        ? network.edges.filter((e) => e.data.edge_type === 'uses' && e.data.target === selected.id)
            .map((e) => String(e.data.source).replace('ig_profile:', ''))
        : [];

    return (
        <div className="h-full flex flex-col bg-zinc-50 dark:bg-zinc-950">
            {/* Header */}
            <div className="flex flex-col gap-1 px-5 py-3 border-b border-zinc-200 dark:border-zinc-800">
                <div className="flex items-center gap-3 flex-wrap">
                    <Share2 className="w-5 h-5 text-pink-500" />
                    <h1 className="text-base font-semibold">Compare IG accounts</h1>
                    <span className="text-[11px] text-muted-foreground hidden sm:inline">after Instagram leads</span>
                    <div className="ml-auto flex items-center gap-2">
                        <select
                            value={caseId} onChange={(e) => setCaseId(e.target.value)}
                            className="text-xs bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 rounded px-2 py-1 max-w-[280px]"
                        >
                            {cases.length === 0 && <option value="">No cases</option>}
                            {cases.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                        </select>
                        <button onClick={() => load(caseId)} disabled={!caseId || loading}
                            className="text-xs flex items-center gap-1 px-2 py-1 rounded border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-50">
                            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
                        </button>
                        <button onClick={sendToGraph} disabled={!caseId || promoting || profiles.length === 0}
                            title="Add harvested profiles and shared wallets/UPI/phones into this case’s graph"
                            className="text-xs flex items-center gap-1 px-2 py-1 rounded bg-primary text-primary-foreground hover:bg-brand-gold-hover disabled:opacity-50">
                            <Send className={`w-3 h-3 ${promoting ? 'animate-pulse' : ''}`} /> Add links to case graph
                        </button>
                        <button
                            type="button"
                            onClick={() => caseId && navigate(`/cases/${caseId}/canvas`)}
                            disabled={!caseId}
                            title="Open this case’s Fusion Canvas (real graph — not the /fusion sandbox)"
                            data-testid="ig-open-case-canvas"
                            className="text-xs flex items-center gap-1 px-2 py-1 rounded border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-50"
                        >
                            <ExternalLink className="w-3 h-3" /> Fusion Canvas
                        </button>
                        <button onClick={() => navigate('/instagram')}
                            className="text-xs flex items-center gap-1 px-2 py-1 rounded bg-brand-ink text-white hover:bg-neutral-900 dark:bg-white/10 dark:text-foreground dark:hover:bg-white/15">
                            <Camera className="w-3 h-3" /> Instagram leads
                        </button>
                    </div>
                </div>
                {/* Mini path so this page is not orphaned */}
                <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                    <span className="px-1.5 py-0.5 rounded bg-zinc-200/80 dark:bg-zinc-800">1. Cases</span>
                    <span>→</span>
                    <button type="button" onClick={() => navigate('/instagram')} className="px-1.5 py-0.5 rounded bg-primary/15 text-foreground hover:underline">
                        2. Instagram leads
                    </button>
                    <span>→</span>
                    <span className="px-1.5 py-0.5 rounded bg-primary/15 text-foreground font-medium">
                        3. Compare accounts (you are here)
                    </span>
                    <span>→</span>
                    <button
                        type="button"
                        onClick={() => caseId && navigate(`/cases/${caseId}/canvas`)}
                        disabled={!caseId}
                        className="px-1.5 py-0.5 rounded bg-zinc-200/80 dark:bg-zinc-800 hover:underline disabled:opacity-50"
                    >
                        4. Fusion Canvas
                    </button>
                    <span>→</span>
                    <button type="button" onClick={() => navigate('/workbench')} className="px-1.5 py-0.5 rounded bg-zinc-200/80 dark:bg-zinc-800 hover:underline">
                        5. Workbench (trace wallets)
                    </button>
                </div>
                <p className="text-[12px] text-muted-foreground max-w-3xl">
                    Shows when different Instagram accounts in this case publish the same wallet, UPI, or phone —
                    a clue they may be one operation. After “Add links to case graph”, open{' '}
                    <strong className="font-medium text-foreground">Fusion Canvas</strong> for this case
                    (Cases → your case → Fusion Canvas). Sidebar “Fusion” is a synthetic sandbox and will not show your Instagram entities.
                </p>
            </div>

            {error && <div className="px-5 py-2 text-xs text-red-500 bg-red-500/10">{error}</div>}
            {promoteMsg && (
                <div className="px-5 py-2 text-xs text-foreground bg-primary/10 flex flex-wrap items-center gap-2">
                    <span className="flex-1 min-w-0">{promoteMsg}</span>
                    {caseId && !promoteMsg.toLowerCase().includes('failed') && (
                        <button
                            type="button"
                            onClick={() => navigate(`/cases/${caseId}/canvas`)}
                            className="shrink-0 inline-flex items-center gap-1 font-semibold underline hover:no-underline"
                            data-testid="ig-promote-open-case-canvas"
                        >
                            Open Fusion Canvas <ExternalLink className="w-3 h-3" />
                        </button>
                    )}
                </div>
            )}

            <div className="flex-1 min-h-0 flex">
                {/* Left — profile list */}
                <div className="w-72 shrink-0 border-r border-zinc-200 dark:border-zinc-800 overflow-y-auto">
                    <div className="px-3 py-2 flex items-center gap-2 text-[11px] uppercase tracking-wide text-muted-foreground border-b border-zinc-200 dark:border-zinc-800">
                        <Users className="w-3 h-3" /> Harvested profiles ({profiles.length})
                    </div>
                    {profiles.length === 0 && !loading && (
                        <div className="p-4 text-center text-xs text-muted-foreground space-y-3">
                            <p className="font-medium text-foreground/80">No profiles on this case yet</p>
                            <p>
                                Harvest at least two Instagram accounts with shared payment clues
                                (same wallet, UPI, or phone) to see links here.
                            </p>
                            <button onClick={() => navigate('/instagram')} className="text-pink-500 underline font-medium">
                                Go to Instagram leads →
                            </button>
                        </div>
                    )}
                    {profiles.map((p) => (
                        <button key={p.handle} onClick={() => focusHandle(p.handle)}
                            className={`w-full text-left px-3 py-2 border-b border-zinc-100 dark:border-zinc-800/60 hover:bg-zinc-100 dark:hover:bg-zinc-800/50 ${selectedProfile?.handle === p.handle ? 'bg-primary/10' : ''}`}>
                            <div className="flex items-center gap-2">
                                {p.avatar_url
                                    ? <img src={p.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover" />
                                    : <div className="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center text-primary text-xs font-bold">{p.handle[0]?.toUpperCase()}</div>}
                                <div className="min-w-0 flex-1">
                                    <div className="text-xs font-semibold truncate">@{p.handle}</div>
                                    {p.full_name && <div className="text-[10px] text-muted-foreground truncate">{p.full_name}</div>}
                                </div>
                                {p.resolved_identity
                                    ? (
                                        <span title="Identity resolved from phone/email pivots" className="inline-flex">
                                            <ShieldCheck className="w-3.5 h-3.5 text-green-500" aria-label="identity resolved" />
                                        </span>
                                    )
                                    : null}
                            </div>
                            <div className="mt-1 flex flex-wrap gap-1">
                                {Object.entries(p.by_type || {}).map(([t, n]) => (
                                    <span key={t} className="text-[9px] px-1 py-0.5 rounded bg-zinc-200 dark:bg-zinc-800 text-muted-foreground">
                                        {t.replace('crypto_address', 'wallet').replace('upi_id', 'upi')}:{n}
                                    </span>
                                ))}
                            </div>
                        </button>
                    ))}
                </div>

                {/* Center — network graph */}
                <div className="flex-1 min-w-0 relative">
                    {counts && (
                        <div className="absolute top-2 left-2 z-10 flex gap-2 text-[10px]">
                            <span className="px-2 py-1 rounded bg-white/80 dark:bg-zinc-900/80 border border-zinc-200 dark:border-zinc-700">{counts.profiles} profiles</span>
                            <span className="px-2 py-1 rounded bg-white/80 dark:bg-zinc-900/80 border border-zinc-200 dark:border-zinc-700 flex items-center gap-1"><Link2 className="w-3 h-3 text-primary" />{counts.profile_links} links</span>
                            <span className="px-2 py-1 rounded bg-white/80 dark:bg-zinc-900/80 border border-zinc-200 dark:border-zinc-700">{counts.shared_artefacts} shared artefacts</span>
                        </div>
                    )}
                    {elements.length > 0 ? (
                        <div className="w-full h-full bg-white dark:bg-zinc-950">
                            <CytoscapeComponent
                                key={`${caseId}-${theme}` /* fresh instance per case/theme so layout + palette re-apply */}
                                elements={elements as any}
                                stylesheet={networkStyle as any}
                                layout={LAYOUT}
                                cy={registerCy}
                                style={{ width: '100%', height: '100%' }}
                            />
                        </div>
                    ) : (
                        <div className="h-full flex items-center justify-center text-center text-sm text-muted-foreground p-8">
                            {loading ? 'Loading network…'
                                : profiles.length > 0
                                    ? (
                                        <div className="max-w-sm space-y-2">
                                            <AlertTriangle className="w-5 h-5 mx-auto text-amber-500" />
                                            <p className="font-medium text-foreground/80">No shared payment clues yet</p>
                                            <p className="text-[12px]">
                                                These accounts don&apos;t publish the same wallet, UPI, phone, or handle.
                                                Save more leads under each handle from Instagram leads — links appear automatically.
                                            </p>
                                            <button onClick={() => navigate('/instagram')} className="text-pink-500 underline text-xs">
                                                Find more leads →
                                            </button>
                                        </div>
                                    )
                                    : (
                                        <div className="max-w-sm space-y-2">
                                            <p className="font-medium text-foreground/80">Build the network from harvested accounts</p>
                                            <p className="text-[12px]">
                                                1) Open Instagram leads · 2) Save extracts for two+ handles on this case ·
                                                3) Return here to see shared wallets / UPI / phones.
                                            </p>
                                            <button onClick={() => navigate('/instagram')} className="text-pink-500 underline text-xs font-medium">
                                                Start finding leads →
                                            </button>
                                        </div>
                                    )}
                        </div>
                    )}
                </div>

                {/* Right — detail */}
                <div className="w-80 shrink-0 border-l border-zinc-200 dark:border-zinc-800 overflow-y-auto">
                    {!selected && <div className="p-4 text-xs text-muted-foreground">Select a profile or a shared node to inspect it.</div>}

                    {selected?.kind === 'ig_profile' && (
                        <div className="p-4 space-y-3">
                            <div className="flex items-center gap-2">
                                <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center text-primary font-bold">{String(selected.handle)[0]?.toUpperCase()}</div>
                                <div>
                                    <div className="text-sm font-semibold">@{selected.handle}</div>
                                    {selectedProfile?.full_name && <div className="text-[11px] text-muted-foreground">{selectedProfile.full_name}</div>}
                                </div>
                            </div>
                            {selectedProfile?.bio && <p className="text-[11px] text-muted-foreground whitespace-pre-wrap">{selectedProfile.bio}</p>}
                            {selectedProfile?.external_url && (
                                <a href={selectedProfile.external_url} target="_blank" rel="noreferrer" className="text-[11px] text-sky-500 flex items-center gap-1 truncate">
                                    <ExternalLink className="w-3 h-3" /> {selectedProfile.external_url}
                                </a>
                            )}
                            <div>
                                <div className="text-[10px] uppercase text-muted-foreground mb-1">Extracted artefacts</div>
                                <div className="flex flex-wrap gap-1">
                                    {Object.entries(selectedProfile?.by_type || {}).map(([t, n]) => (
                                        <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-200 dark:bg-zinc-800">{t.replace('crypto_address', 'wallet').replace('upi_id', 'upi')}: {n}</span>
                                    ))}
                                    {(!selectedProfile || selectedProfile.artifact_count === 0) && <span className="text-[10px] text-muted-foreground">none</span>}
                                </div>
                            </div>
                            {selectedProfile?.resolved_identity && (
                                <div className="text-[11px] rounded border border-green-500/30 bg-green-500/10 p-2 space-y-1">
                                    <div className="font-medium text-green-700 dark:text-green-400 flex items-center gap-1">
                                        <ShieldCheck className="w-3.5 h-3.5" /> Resolved identity (lead)
                                    </div>
                                    <pre className="whitespace-pre-wrap font-mono text-[10px] max-h-36 overflow-auto">
                                        {JSON.stringify(selectedProfile.resolved_identity, null, 2)}
                                    </pre>
                                </div>
                            )}
                            <button
                                type="button"
                                disabled={resolving || !selected?.handle}
                                onClick={() => resolveIdentity(String(selected.handle))}
                                title="Look up harvested phones/emails in the platform identity index"
                                className="w-full text-xs flex items-center justify-center gap-1 px-2 py-1.5 rounded bg-sky-600 text-white hover:bg-sky-500 disabled:opacity-50">
                                <Fingerprint className={`w-3.5 h-3.5 ${resolving ? 'animate-pulse' : ''}`} />
                                {resolving ? 'Resolving…' : 'Resolve identity from phone/email'}
                            </button>
                            <p className="text-[10px] text-muted-foreground">
                                Uses phones and emails already harvested from this profile. Results stay leads until confirmed.
                            </p>
                        </div>
                    )}

                    {selected && selected.kind !== 'ig_profile' && (
                        <div className="p-4 space-y-3">
                            <div className="text-[10px] uppercase text-muted-foreground">Shared {String(selected.kind).replace('address', 'wallet')}</div>
                            <div className="font-mono text-[11px] break-all bg-zinc-100 dark:bg-zinc-900 rounded p-2">{selected.label}</div>
                            {selected.chain && <div className="text-[10px] text-muted-foreground">chain: {selected.chain}</div>}
                            <div>
                                <div className="text-[10px] uppercase text-muted-foreground mb-1 flex items-center gap-1">
                                    <Link2 className="w-3 h-3 text-amber-500" /> Shared by {sharedByHandles.length} profiles
                                </div>
                                {sharedByHandles.map((h) => (
                                    <button key={h} onClick={() => focusHandle(h)} className="block w-full text-left text-[11px] px-2 py-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800">@{h}</button>
                                ))}
                            </div>
                            <p className="text-[10px] text-muted-foreground italic">A shared wallet/UPI/phone across profiles is strong evidence they're one operation.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
